import React from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, Plus, Loader2, HardDrive, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import InputText from "@/components/common/InputText";
import TaskAttachment from "../../taskAttachment";
import type { Task } from "@/api/mutation/useTaskCreateMutation";
import DropboxSvg from "@/assets/svg/Dropbox.svg";
import GoogleSvg from "@/assets/svg/google.svg";

interface TaskAttachmentsSectionProps {
  task?: Task;
  refetch: () => void;
  allowed: boolean;
  onUploadComputerFiles: (files: FileList | File[] | null) => void;
  onOpenDropboxPicker: () => void;
  onOpenGoogleDrivePicker: () => void;
  onAddLinkAttachment: (link: { name: string; url: string }) => void;
  isUploading: boolean;
  isLinkPending?: boolean;
}

export const TaskAttachmentsSection: React.FC<TaskAttachmentsSectionProps> = ({
  task,
  refetch,
  allowed,
  onUploadComputerFiles,
  onOpenDropboxPicker,
  onOpenGoogleDrivePicker,
  onAddLinkAttachment,
  isUploading,
  isLinkPending = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [linkName, setLinkName] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkType, setLinkType] = React.useState<"WEB" | "LOCAL">("WEB");

  const attachmentCount = task?.documentAttachments?.length || 0;

  const handleLinkSubmit = () => {
    if (!linkName.trim() || !linkUrl.trim()) return;
    onAddLinkAttachment({ name: linkName.trim(), url: linkUrl.trim() });
    setLinkName("");
    setLinkUrl("");
    setShowLinkInput(false);
    setIsPopoverOpen(false);
  };

  return (
    <div className="flex flex-col gap-2 p-3 bg-white border border-slate-200/90 rounded-xl shadow-2xs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Paperclip className="w-3.5 h-3.5 text-slate-400" />
          <span>{t("attachment")}</span>
          <span className="text-[11px] font-medium text-slate-400">
            ({attachmentCount})
          </span>
          {isUploading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 ml-1" />}
        </div>

        {allowed && (
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild disabled={isUploading}>
              <Button
                type="button"
                variant="none"
                size="sm"
                className="flex items-center gap-1 h-7 px-2.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-2xs transition-all cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5 text-white" />
                <span>{t("add")}</span>
              </Button>
            </PopoverTrigger>

            <PopoverContent align="end" className="w-64 p-2 z-[9999] shadow-xl border border-slate-200 rounded-xl">
              {showLinkInput ? (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold text-slate-800">{t("add_link")}</span>
                  <InputText
                    autoFocus
                    placeholder={t("link_name") || "Link name"}
                    value={linkName}
                    onChange={(e) => setLinkName(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        checked={linkType === "WEB"}
                        onChange={() => setLinkType("WEB")}
                        className="text-primary-600"
                      />
                      <span>{t("web_url")}</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        checked={linkType === "LOCAL"}
                        onChange={() => setLinkType("LOCAL")}
                        className="text-primary-600"
                      />
                      <span>{t("local_network")}</span>
                    </label>
                  </div>
                  <InputText
                    placeholder={linkType === "WEB" ? "https://..." : "\\\\server\\share\\..."}
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <div className="flex items-center justify-end gap-1.5 mt-1">
                    <Button
                      type="button"
                      variant="none"
                      size="sm"
                      onClick={() => setShowLinkInput(false)}
                      className="h-7 px-2 text-xs text-slate-500"
                    >
                      {t("back")}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={handleLinkSubmit}
                      disabled={!linkName || !linkUrl || isLinkPending}
                      className="h-7 px-3 text-xs font-semibold"
                    >
                      {t("add")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setIsPopoverOpen(false);
                      fileInputRef.current?.click();
                    }}
                    className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                    <span>{t("from_computer")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsPopoverOpen(false);
                      onOpenDropboxPicker();
                    }}
                    className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <img src={DropboxSvg} className="w-3.5 h-3.5" alt="Dropbox" />
                    <span>{t("from_dropbox")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsPopoverOpen(false);
                      onOpenGoogleDrivePicker();
                    }}
                    className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <img src={GoogleSvg} className="w-3.5 h-3.5" alt="Google Drive" />
                    <span>{t("from_google_drive")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowLinkInput(true)}
                    className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Link2 className="w-3.5 h-3.5 text-slate-500" />
                    <span>{t("from_link")}</span>
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Hidden file input for native explorer pick */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const fileArray = e.target.files ? Array.from(e.target.files) : [];
          e.target.value = "";
          if (fileArray.length > 0) {
            onUploadComputerFiles(fileArray);
          }
        }}
      />

      {/* Attachments List */}
      {attachmentCount > 0 ? (
        <div className="mt-1">
          <TaskAttachment task={task} refetch={refetch} allowed={allowed} />
        </div>
      ) : (
        <span className="text-xs text-slate-400 italic py-1">
          {t("no_attachments_added")}
        </span>
      )}
    </div>
  );
};

export default TaskAttachmentsSection;
